<template>
    <div>
        <div class="datepicker-bar">
            <button
                type="button"
                class="prev-button"
                :disabled="isPrevDisabled"
                :aria-disabled="isPrevDisabled"
                @click="decrementDate"
            >
                <IconArrow class="is-aria-hidden" width="18" height="18" />
                <span class="visuallyhidden">{{ t('go_to') }} {{ prevDay }}</span>
            </button>
            <div class="datepicker-trigger">
                <button
                    id="datepicker-trigger"
                    type="button"
                    class="datepicker-button"
                    :aria-label="`${format(parsedDate, 'EEEE d MMMM yyyy')}, ${t('choose_date')}`"
                    @click="showPicker"
                >
                    <IconDate class="is-aria-hidden date-icon" width="24" height="24" />
                    <span>
                        <span class="day-of-week">{{ format(parsedDate, 'EEEE') }}</span>
                        &nbsp;{{ format(parsedDate, 'd MMM yyyy') }}
                    </span>
                    <IconDropdownArrow class="is-aria-hidden chevron" width="12" height="8" />
                </button>
                <input
                    ref="input"
                    :value="modelValue"
                    aria-hidden="true"
                    inert
                    class="visuallyhidden"
                    required
                    type="date"
                    :min="startDateFormat"
                    :max="endDateFormat"
                    @change="onChange"
                />
            </div>
            <button
                type="button"
                class="next-button"
                :disabled="isNextDisabled"
                :aria-disabled="isNextDisabled"
                @click="incrementDate"
            >
                <IconArrow class="is-aria-hidden" width="18" height="18" />
                <span class="visuallyhidden">{{ t('go_to') }} {{ nextDay }}</span>
            </button>
        </div>
        <p v-if="errorMessage" class="callout invalid-date alert">
            {{ t('invalid_date', { date: invalidDate, minDate: startDateFormat, maxDate: endDateFormat }) }}
        </p>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';

import { format, subHours, subMinutes, addDays, subDays, parseISO } from '@@/js/utils/dateFns';
import { constants } from '@@/js/utils/constants';

import IconArrow from '@@/icons/arrow.svg';
import IconDate from '@@/icons/date.svg';
import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const modelValue = defineModel({ type: String, required: true });

const props = defineProps({
    invalidDate: {
        type: String,
        default: null,
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            choose_date: 'Cliquer pour choisir une autre date de voyage',
            go_to: 'Passez au',
            invalid_date:
                'La date choisie, {date}, excède la période de disponibilité des horaires, soit du {minDate} au {maxDate}.',
        },
        en: {
            choose_date: 'Click to change travel date',
            go_to: 'Go to',
            invalid_date:
                'The chosen date, {date}, exceed the schedules availability period: from {minDate} to {maxDate}.',
        },
    },
});

const parsedDate = computed(() =>
    typeof modelValue.value === 'string' ? parseISO(modelValue.value) : modelValue.value,
);

const startDate = subHours(subMinutes(new Date(), 15), 4);
const range = constants.CALENDAR_RANGE;
const unlimited = constants.UNLIMITED_CALENDAR;

const endDate = computed(() => addDays(startDate, range));

const startDateFormat = computed(() => format(startDate, 'yyyy-MM-dd'));

const endDateFormat = computed(() => format(endDate.value, 'yyyy-MM-dd'));

const nextDay = computed(() => {
    const next = addDays(parsedDate.value, 1);
    return format(next, 'd MMMM yyyy');
});

const prevDay = computed(() => {
    const prev = subDays(parsedDate.value, 1);
    return format(prev, 'd MMMM yyyy');
});

const isNextDisabled = computed(() => parsedDate.value >= endDate);
const isPrevDisabled = computed(() => parsedDate.value <= startDate);

const errorMessage = computed(() => props.invalidDate && unlimited !== 'true');

const input = ref();

function showPicker() {
    input.value.showPicker();
}

function onChange(event) {
    const val = event.currentTarget.value;
    if (val) {
        modelValue.value = format(parseISO(val), 'yyyy-MM-dd');
    } else {
        modelValue.value = format(new Date(), 'yyyy-MM-dd');
    }
}

function incrementDate() {
    if (!isNextDisabled.value) {
        const newDate = addDays(parsedDate.value, 1);
        modelValue.value = format(newDate, 'yyyy-MM-dd');
    }
}

function decrementDate() {
    if (!isPrevDisabled.value) {
        const newDate = subDays(parsedDate.value, 1);
        modelValue.value = format(newDate, 'yyyy-MM-dd');
    }
}
</script>

<style lang="scss" scoped>
.datepicker-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    @include breakpoint(medium) {
        justify-content: flex-start;
    }
}
.datepicker-button {
    @include rem-typo(14px);
    display: flex;
    align-items: center;
    gap: 16px;
    font-weight: 600;
    padding: 12px 16px;
    border-radius: 12px;
    background-color: $color-black-6;
    border: 0;
    @include breakpoint(medium) {
        @include rem-typo(16px);
    }
}
.is-aria-hidden {
    pointer-events: none;
}
.date-icon {
    fill: $color-black-40;
}
.next-button,
.prev-button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    background-color: $color-blue-90;
    color: $color-white;
    border: 0;
    flex-shrink: 0;
    &:hover,
    &:focus-visible {
        background-color: $color-blue-100;
    }
    &:disabled {
        background-color: $color-black-20;
        color: $color-black-80;
    }
}
.prev-button {
    svg {
        transform: rotate(180deg);
    }
}
.day-of-week {
    text-transform: capitalize;
    pointer-events: none;
}
.invalid-date {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0;
    border: 1px solid $color-black-10;
}
</style>
